/**
 *
 * @module purchaseApi
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/purchase.md
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/PurchaseApi.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Subscription from './../subscription/entry';
import PurchaseManager from './purchaseManager';
import RedeemRetryHandler from './redeemRetryHandler';
import Receipt from './receipt';

const Util = Services.Util;
const SubscriptionManager = Subscription.SubscriptionManager;
const DustLogUtility = Services.Internal.Dust.DustLogUtility;
const PurchaseApiDustUrnReference = Services.Internal.Dust.DustUrnReference.purchase.purchaseApi;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class PurchaseApi
 * @desc Provides a way to redeem and restore purchases.
 *
 */
export default class PurchaseApi {

    /**
     *
     * @param {PurchaseManager} purchaseManager
     * @param {SDK.Subscription.SubscriptionManager} subscriptionManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(purchaseManager, subscriptionManager, logger = Logging) {

        const errorMessage = {
            ctor: 'PurchaseApi(purchaseManager, subscriptionManager, logger)',
            get purchaseManager() {
                return `${this.ctor} needs PurchaseManager instance`;
            },
            get subscriptionManager() {
                return `${this.ctor} needs SDK.Subscription.SubscriptionManager instance`;
            }
        };

        instanceStrict(purchaseManager, PurchaseManager, errorMessage.purchaseManager, InvalidArgumentException);
        instanceStrict(subscriptionManager, SubscriptionManager, errorMessage.subscriptionManager, InvalidArgumentException); // eslint-disable-line

        /**
         *
         * @access private
         * @type {PurchaseManager}
         *
         */
        this.purchaseManager = purchaseManager;

        /**
         *
         * @access private
         * @type {SDK.Subscription.SubscriptionManager}
         *
         */
        this.subscriptionManager = subscriptionManager;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc used to enable dust logging
         *
         */
        this.dustEnabled = Util.getSafe(() => this.purchaseManager.client.dustEnabled);

        this.logger.info(PurchaseApi.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {Receipt} receipt - The Receipt for the purchase being redeemed.
     * @desc Redeem new purchases with the activation service and apply them to the access context.
     * @throws {ActivationFailedException} The service failed to activate the receipt.
     * @note This error should be thrown if the service responds with a 202 code and temporaryAccessGranted: false
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<AccessStatus>} Returns an asynchronous action that
     * will send a notification when it is complete.
     *
     */
    redeem(receipt) {

        const errorMessage = {
            method: 'PurchaseApi.redeem(receipt)',
            get receipt() {
                return `${this.method} needs Receipt instance`;
            }
        };

        const { dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: PurchaseApi.name,
            urn: PurchaseApiDustUrnReference.redeem
        });

        instanceStrict(receipt, Receipt, errorMessage.receipt, InvalidArgumentException);

        const { purchaseManager } = this;

        const handler = new RedeemRetryHandler(purchaseManager, receipt);

        return handler.run().catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {Receipt} receipt - The Receipt for the purchase being restored.
     * @desc Restore existing purchases by validating them with the activation
     * service and applying them to the access context.
     * @throws {ActivationFailedException} The service failed to activate the receipt.
     * @note This error should be thrown if the service responds with a 202 code and temporaryAccessGranted: false
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<AccessStatus>} Returns an asynchronous action that
     * will send a notification when it is complete.
     *
     */
    restore(receipt) {

        const errorMessage = {
            method: 'PurchaseApi.restore(receipt)',
            get receipt() {
                return `${this.method} needs Receipt instance`;
            }
        };

        const { dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: PurchaseApi.name,
            urn: PurchaseApiDustUrnReference.restore
        });

        instanceStrict(receipt, Receipt, errorMessage.receipt, InvalidArgumentException);

        const { purchaseManager } = this;

        return purchaseManager.restorePurchases(receipt.receiptCredentials).catch((ex) => {

            return dustEnabled ? Promise.reject(dustLogUtility.captureErrors(ex)) : Promise.reject(ex);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

}



// WEBPACK FOOTER //
// ./src/purchase/purchaseApi.js